import React, { FC, useEffect, useState } from 'react';
import { NavLink, RouteComponentProps, withRouter } from 'react-router-dom';
import { WithStyles, createStyles, withStyles } from '@material-ui/core/styles';
import { Divider, Tooltip } from '@material-ui/core';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import AccountCircleIcon from '@material-ui/icons/AccountCircleOutlined';
import HelpDialog from './dashboard/HelpDialog';
import SavedSearchCard from './dashboard/SavedSearchCard';
import DashboardModal from './dashboard/DashboardModal';
import { trackAction } from '../services/analytics/AnalyticsService';
import API from '../services/ApiInstance';
import ApiRoutes from '../services/ApiRoutes';
import { ROUTES } from '../constants';
import { toast } from 'react-toastify';
import {
  HasFilters,
  GenerateFilterParams,
  FilterTypes,
  filters,
} from '../signals/filterService';
import { SavedSearchDto, ThemeConfigDto } from '../api/originalMappings';
import { NotificationPanel } from './notification-panel/NotificationPanel';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import ShowChartIcon from '@material-ui/icons/ShowChart';
import TrackChangesIcon from '@material-ui/icons/TrackChanges';
import Spacer from './common/Spacer';
import useAuthorization from '../hooks/useAuthorisationManager';

interface Props extends WithStyles<typeof styles>, RouteComponentProps {
  loggedIn: boolean;
  logoutCallback: (e: any) => void;
  themeConfig: ThemeConfigDto;
}

const styles = (theme: { zIndex: { drawer: number } }) =>
  createStyles({
    root: {
      flexGrow: 1,
      padding: 0,
      color: 'white',
    },
    grow: {
      flexGrow: 1,
      whiteSpace: 'nowrap',
      display: 'inherit',
      justifyContent: 'center',
    },
    menuItem: {
      display: 'flex',
      alignItems: 'center',
      padding: 0,
      minHeight: 38,
      '&:hover': {
        backgroundColor: 'var(--color-primary-tint)',
      },
    },
    menuLink: {
      textDecoration: 'none',
      padding: '8px 16px',
      display: 'block',
      width: '100%',
      fontSize: '13px',
      fontWeight: 500,
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      color: 'var(--color-text-primary)',
      transition: 'color 0.15s ease',
      '&:hover': {
        color: 'var(--color-primary)',
      },
    },
    activeMenuButton: {
      backgroundColor: 'var(--color-primary-tint)',
      color: 'var(--color-primary) !important',
      fontWeight: 600,
    },
    menuDivider: {
      margin: '4px 12px',
      backgroundColor: 'var(--color-border)',
    },

    iconButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      padding: '6px 14px',
      borderRadius: '6px',
      fontSize: '13px',
      fontWeight: 500,
      color: 'var(--color-text-secondary)',
      cursor: 'pointer',
      transition: 'all 0.15s ease',
      textDecoration: 'none',
      '&:hover': {
        backgroundColor: 'var(--color-muted-bg)',
        color: 'var(--color-text-primary)',
      },
    },
    headerIconActive: {
      backgroundColor: 'var(--color-primary-tint) !important',
      color: 'var(--color-primary) !important',
      fontWeight: 600,
    },
    progressIcon: {
      position: 'absolute',
      marginLeft: '49%',
      marginTop: 380,
    },
    appBar: {
      zIndex: theme.zIndex.drawer + 1,
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    },
    logo: {
      maxHeight: 32,
      marginRight: 8,
      marginLeft: 10,
    },
    brandName: {
      fontWeight: 700,
      fontSize: '1.25rem',
      color: 'var(--color-text-primary)',
      letterSpacing: '-0.3px',
      marginRight: 24,
    },
    navLinks: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
    navLink: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      padding: '6px 14px',
      borderRadius: '6px',
      fontSize: '13px',
      fontWeight: 500,
      color: 'var(--color-text-secondary)',
      textDecoration: 'none',
      transition: 'all 0.15s ease',
      '&:hover': {
        backgroundColor: 'var(--color-muted-bg)',
        color: 'var(--color-text-primary)',
      },
    },
    navLinkActive: {
      backgroundColor: 'var(--color-primary-tint)',
      color: 'var(--color-primary)',
      fontWeight: 600,
    },
    button: {
      margin: '0 5px',
      padding: '7px 11px',
    },
  });

const Header: FC<Props> = ({
  classes,
  location,
  loggedIn,
  logoutCallback,
  themeConfig,
}) => {
  const { hasAccess } = useAuthorization();
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [savedSearches, setSavedSearches] = useState<Array<SavedSearchDto>>([]);
  const [hasFilters, setHasFilter] = useState(false);
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(
    function checkIfFiltersExist() {
      updateFiltersState();
    },
    [filters.value]
  );

  const updateFiltersState = () => {
    setFiltersChanged(true);
    setHasFilter(HasFilters());
  };

  const showMenu = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const hideMenu = () => {
    setAnchorEl(null);
  };

  const handleHelpDialogClose = () => {};

  const setSearchModal = (_showSearchModal: boolean) => () =>
    setShowSearchModal(_showSearchModal);

  const saveSearch = () => {
    trackAction('saved search', 'save');
    API.post(ApiRoutes.saveSearch, GenerateFilterParams(FilterTypes.report))
      .then((_) => {
        toast.success('Search saved');
        getSearches();
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const deleteSearch = (search: string) => {
    API.del(ApiRoutes.deleteSearch, search).then((_) => {
      toast.success('Search deleted');
      getSearches();
    });
  };

  const getSearches = () => {
    API.get<any, SavedSearchDto[]>(ApiRoutes.getSavedSearches)
      .then((res) => setSavedSearches(res))
      .catch((err) => {
        console.error(err);
      });
  };

  const UserMenu = () => {
    const openSearch = setSearchModal(true);

    return (
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={hideMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        getContentAnchorEl={null}
        PaperProps={{
          style: {
            borderRadius: 10,
            marginTop: 6,
            minWidth: 200,
            boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
            border: '1px solid var(--color-border)',
            fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
          },
        }}
      >
        {/* Profile & Searches */}
        <MenuItem onClick={hideMenu} className={classes.menuItem}>
          <NavLink to={ROUTES.UserProfile} className={classes.menuLink} activeClassName={classes.activeMenuButton}>
            Profile
          </NavLink>
        </MenuItem>
        <MenuItem
          key="SavedSearches"
          className={classes.menuItem}
          onClick={() => { openSearch(); hideMenu(); }}
        >
          <span className={classes.menuLink}>Saved Searches</span>
        </MenuItem>
        <MenuItem
          key="SaveSearch"
          className={classes.menuItem}
          onClick={() => { saveSearch(); hideMenu(); }}
          disabled={!hasFilters}
        >
          <span className={classes.menuLink}>Save Search</span>
        </MenuItem>

        {/* Data Management */}
        <Divider className={classes.menuDivider} />
        {hasAccess(ROUTES.Upload) && (
          <MenuItem onClick={hideMenu} className={classes.menuItem}>
            <NavLink to={ROUTES.Upload} className={classes.menuLink} activeClassName={classes.activeMenuButton}>
              Upload Data
            </NavLink>
          </MenuItem>
        )}
        {hasAccess(ROUTES.Facilities) && (
          <MenuItem onClick={hideMenu} className={classes.menuItem}>
            <NavLink to={ROUTES.Facilities} className={classes.menuLink} activeClassName={classes.activeMenuButton}>
              Facilities
            </NavLink>
          </MenuItem>
        )}
        <MenuItem onClick={hideMenu} className={classes.menuItem}>
          <NavLink to={ROUTES.Actions} className={classes.menuLink} activeClassName={classes.activeMenuButton}>
            Actions
          </NavLink>
        </MenuItem>
        {hasAccess(ROUTES.Aliases) && (
          <MenuItem onClick={hideMenu} className={classes.menuItem}>
            <NavLink to={ROUTES.Aliases} className={classes.menuLink} activeClassName={classes.activeMenuButton}>
              Report Type Aliases
            </NavLink>
          </MenuItem>
        )}

        {/* Configuration */}
        <Divider className={classes.menuDivider} />
        {hasAccess(ROUTES.DashboardLayout) && (
          <MenuItem onClick={hideMenu} className={classes.menuItem}>
            <NavLink to={ROUTES.DashboardLayout} className={classes.menuLink} activeClassName={classes.activeMenuButton}>
              Dashboard Customisation
            </NavLink>
          </MenuItem>
        )}
        {hasAccess(ROUTES.WidgetsAndFilters) && (
          <MenuItem onClick={hideMenu} className={classes.menuItem}>
            <NavLink to={ROUTES.WidgetsAndFilters} className={classes.menuLink} activeClassName={classes.activeMenuButton}>
              Widgets & Filters
            </NavLink>
          </MenuItem>
        )}
        {hasAccess(ROUTES.UsersAndOrganisations) && (
          <MenuItem onClick={hideMenu} className={classes.menuItem}>
            <NavLink to={ROUTES.UsersAndOrganisations} className={classes.menuLink} activeClassName={classes.activeMenuButton}>
              Users & Organisations
            </NavLink>
          </MenuItem>
        )}

        {/* Administration */}
        {(hasAccess(ROUTES.SuperUserPanel) || hasAccess(ROUTES.ActivityLogs)) && (
          <Divider className={classes.menuDivider} />
        )}
        {hasAccess(ROUTES.SuperUserPanel) && (
          <MenuItem onClick={hideMenu} className={classes.menuItem}>
            <NavLink to={ROUTES.SuperUserPanel} className={classes.menuLink} activeClassName={classes.activeMenuButton}>
              Super User Panel
            </NavLink>
          </MenuItem>
        )}
        {hasAccess(ROUTES.ActivityLogs) && (
          <MenuItem onClick={hideMenu} className={classes.menuItem}>
            <NavLink to={ROUTES.ActivityLogs} className={classes.menuLink} activeClassName={classes.activeMenuButton}>
              Activity Logs
            </NavLink>
          </MenuItem>
        )}

        {/* Logout */}
        <Divider className={classes.menuDivider} />
        {loggedIn && (
          <MenuItem
            className={classes.menuItem}
            onClick={(e) => { logoutCallback(e); hideMenu(); }}
          >
            <span className={classes.menuLink} style={{ color: 'var(--color-error)' }}>Logout</span>
          </MenuItem>
        )}
      </Menu>
    );
  };


  return (
    <>
      <AppBar
        position="fixed"
        className={classes.appBar}
        style={{ backgroundColor: 'white' }}
      >
        <Toolbar>
          <img className={classes.logo} src="images/logos/logomark.png" alt="Signals" />
          <span className={classes.brandName}>Signals</span>
          <div className={classes.navLinks}>
            <NavLink
              to={ROUTES.ConversationalUI}
              className={classes.navLink}
              activeClassName={classes.navLinkActive}
              isActive={(_, loc) => loc.pathname === ROUTES.ConversationalUI || loc.pathname === '/'}
            >
              <ChatBubbleOutlineIcon style={{ fontSize: 16 }} />
              Ask
            </NavLink>
            <NavLink
              to={ROUTES.Dashboard}
              className={classes.navLink}
              activeClassName={classes.navLinkActive}
            >
              <ShowChartIcon style={{ fontSize: 16 }} />
              Discover
            </NavLink>
            <NavLink
              to={ROUTES.Trackers}
              className={classes.navLink}
              activeClassName={classes.navLinkActive}
              isActive={(_, loc) => loc.pathname.startsWith('/trackers')}
            >
              <TrackChangesIcon style={{ fontSize: 16 }} />
              Track
            </NavLink>
          </div>
          <Spacer></Spacer>
          <div className={classes.grow}></div>

          <div className={classes.navLinks}>
            <NotificationPanel />
            <span
              onClick={showMenu}
              className={`${classes.iconButton} ${Boolean(anchorEl) ? classes.headerIconActive : ''}`}
            >
              <AccountCircleIcon style={{ fontSize: 16 }} />
              Account
            </span>
          </div>
          {UserMenu()}
        </Toolbar>
      </AppBar>
      <HelpDialog
        type="general"
        open={helpDialogOpen}
        onClose={handleHelpDialogClose}
      />
      <DashboardModal
        handleClose={setSearchModal(false)}
        title="My Saved Searches"
        open={showSearchModal}
      >
        <SavedSearchCard searches={savedSearches} onDelete={deleteSearch} />
      </DashboardModal>
    </>
  );
};

export default withRouter(withStyles(styles)(Header));
